import { range } from "lodash";
import { isNotNil, Nullable } from "@/models/nullable.type";

export const recurModes = [
  "daily",
  "weekly",
  "monthly",
  "unique",
  "hourly",
  "multihourly",
  "biweekly",
  "bimonthly",
];

export const timeModes = ["fixedTime", "filtrStart", "filtrMid"];

export interface Recurrence {
  mode: typeof recurModes[number];
  params: { timeMode?: typeof timeModes[number] } & Record<string, number>;
}

export type TimeMode = typeof timeModes[number];

const defaultParams = {
  timeMode: "fixedTime",
} as Recurrence["params"];

const timeModeValues = {
  daily: {
    96: "filtrStart",
    97: "filtrMid",
    98: "filtrSpread",
    99: "filtrEnd",
  },
  weekly: {
    24: "filtrStart",
    25: "filtrMid",
    26: "filtrSpread",
    27: "filtrEnd",
  },
  bimonthly: {
    12: "filtrStart",
    13: "filtrMid",
    14: "filtrSpread",
    15: "filtrEnd",
  },
} as Record<
  typeof recurModes[number],
  Record<number, typeof timeModes[number]>
>;

const getTimeMode = (
  mode: keyof typeof timeModeValues,
  value: number
): string => {
  return timeModeValues[mode]?.[value] ?? "fixedTime";
};

const getTimeModeValue = (
  mode: keyof typeof timeModeValues,
  timeMode: typeof timeModes[number]
) => {
  return parseFloat(
    Object.entries(timeModeValues[mode]).find(
      ([, value]) => value === timeMode
    )?.[0] ?? "NaN"
  );
};

const recurTypes = [
  {
    mode: "daily",
    serialize: ({ time, timeMode }: { time: number; timeMode: TimeMode }) =>
      timeMode === "fixedTime"
        ? time / 15
        : getTimeModeValue("daily", timeMode),
    deserialize: (payload: number) => ({
      timeMode: getTimeMode("daily", payload),
      time:
        getTimeMode("daily", payload) === "fixedTime"
          ? payload * 15
          : undefined,
    }),
  },
  {
    mode: "weekly",
    serialize: ({
      dayOfWeek,
      time,
      timeMode,
    }: {
      dayOfWeek: number;
      time: number;
      timeMode: TimeMode;
    }) =>
      dayOfWeek +
      ((timeMode === "fixedTime"
        ? time / 60
        : getTimeModeValue("weekly", timeMode)) <<
        3),
    deserialize: (payload: number) => ({
      dayOfWeek: payload & 0b111,
      timeMode: getTimeMode("weekly", payload >> 3),
      time: (payload >> 3) * 60,
    }),
  },
  {
    mode: "monthly",
    serialize: ({ day, time }: { day: number; time: number }) =>
      day - 1 + ((time / 3 / 60) << 5),
    deserialize: (payload: number) => ({
      day: (payload & 0b11111) + 1,
      time: (payload >> 5) * 3 * 60,
    }),
  },
  {
    mode: "unique",
    serialize: () => 0,
    deserialize: () => ({}),
  },
  {
    mode: "hourly",
    serialize: ({ minutes }: { minutes: number }) => minutes,
    deserialize: (payload: number) => ({ minutes: payload & 0b111111 }),
  },
  {
    mode: "multihourly",
    serialize: ({
      frequency,
      minutes,
    }: {
      frequency: number;
      minutes: number;
    }) => frequency - 2 + ((minutes / 5) << 4),
    deserialize: (payload: number) => ({
      frequency: (payload & 0b1111) + 2,
      minutes: (payload >> 4) * 5,
    }),
  },
  {
    mode: "biweekly",
    serialize: ({
      dayOfWeek1,
      dayOfWeek2,
      hour,
    }: {
      dayOfWeek1: number;
      dayOfWeek2: number;
      hour: number;
    }) => dayOfWeek1 + (dayOfWeek2 << 3) + ((hour / 6) << 6),
    deserialize: (payload: number) => ({
      dayOfWeek1: payload & 0b111,
      dayOfWeek2: (payload >> 3) & 0b111,
      hour: (payload >> 6) * 6,
    }),
  },
  {
    mode: "bimonthly",
    serialize: ({
      day,
      hour,
      timeMode,
    }: {
      day: number;
      hour: number;
      timeMode: TimeMode;
    }) =>
      day -
      1 +
      ((timeMode === "fixedTime"
        ? hour / 2
        : getTimeModeValue("bimonthly", timeMode)) <<
        4),
    deserialize: (payload: number) => ({
      day: (payload & 0b1111) + 1,
      timeMode: getTimeMode("bimonthly", payload >> 4),
      hour: (payload >> 4) * 2,
    }),
  },
];

export const deserializeRecurrence = (
  recurMode: Nullable<number>,
  recurDate: Nullable<number>
): Recurrence => {
  if (!isNotNil(recurMode) || !isNotNil(recurDate)) {
    return { mode: "unique", params: { ...defaultParams } };
  }

  const type = recurTypes[recurMode];
  if (!type) {
    throw Error(`Unknown recurMode: ${recurMode}`);
  }

  return {
    mode: type.mode,
    params: {
      ...defaultParams,
      ...type.deserialize(recurDate),
    } as Recurrence["params"],
  };
};

export const serializeRecurrence = (
  recurrence: Recurrence,
  flags: number,
  recModeFirstBit: number
): { custFlags: number; otherFlags?: number } => {
  const recMode = recurModes.indexOf(recurrence.mode);

  return {
    custFlags:
      (flags & (Math.pow(2, 16) - 1 - (0b111 << recModeFirstBit))) +
      (recMode << recModeFirstBit),
    otherFlags: recurTypes[recMode].serialize(recurrence.params as any) << 8,
  };
};

export const makeMultihourlyExamples = (frequency: number, minutes: number) => {
  const examples = range(0, 24, frequency).map(
    (h) => `${h}h${minutes.toString().padStart(2, "0")}`
  );
  return (
    examples.splice(0, 4).join(", ") + (examples.length > 4 ? ", ..." : "")
  );
};
